import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, PaymentRequestButtonElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { X, ShieldCheck, Loader2, Check, ChevronDown, CreditCard, ChevronUp, Tag, Plus } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { api, formatApiError } from "@/shared/lib/api";
import { toast } from "sonner";
import CheckoutErrorBoundary from "./CheckoutErrorBoundary";

/* Framer-motion backdrop that fades + blurs in/out. */
function CheckoutBackdrop({ onClick }) {
  return (
    <motion.div
      className="absolute inset-0 bg-[#0A1128]/55"
      initial={{ opacity: 0, backdropFilter: "blur(0px)" }}
      animate={{ opacity: 1, backdropFilter: "blur(6px)" }}
      exit={{ opacity: 0, backdropFilter: "blur(0px)" }}
      transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
      onClick={onClick}
      aria-hidden="true"
    />
  );
}

/**
 * extractStripeFailure(ex, ctx)
 * ---------------------------------------------------------------------
 * Builds a rich diagnostic struct from any failure during the Stripe
 * confirm flow — Stripe.js errors (decline / processing_error), HTTP
 * failures from /pay/finalize-subscription, generic JS errors — so the
 * rider sees the exact code/decline_code/status/IDs in the UI instead
 * of just "A processing error occurred." and we can match it to
 * Stripe Dashboard logs in one click.
 *
 * ctx: { stage, intentKind, intentId, paymentMethodId, subscriptionId,
 *        invoiceId, paymentIntentId, setupIntentId, subscriptionStatus,
 *        paymentIntentStatus, setupIntentStatus }
 */
function extractStripeFailure(ex, ctx = {}) {
  const out = {
    stage: ctx.stage || "unknown",
    message: ex?.message || "Payment failed",
    code: ex?.code || null,
    declineCode: ex?.decline_code || ex?.declineCode || null,
    type: ex?.type || null,
    requestId: ex?.requestId || ex?.request_id || ex?.headers?.["request-id"] || null,
    // Stripe.js attaches `payment_intent` / `setup_intent` to error objects
    // when the confirm has a result we can introspect. Backend HTTP errors
    // ride on ex.response.
    paymentIntentId: ex?.payment_intent?.id || ctx.paymentIntentId || null,
    paymentIntentStatus: ex?.payment_intent?.status || ctx.paymentIntentStatus || null,
    setupIntentId: ex?.setup_intent?.id || ctx.setupIntentId || null,
    setupIntentStatus: ex?.setup_intent?.status || ctx.setupIntentStatus || null,
    paymentMethodId: ex?.payment_method?.id || ctx.paymentMethodId || null,
    subscriptionId: ctx.subscriptionId || null,
    subscriptionStatus: ctx.subscriptionStatus || null,
    invoiceId: ctx.invoiceId || null,
    intentKind: ctx.intentKind || null,
    intentId: ctx.intentId || null,
    httpStatus: ex?.response?.status || null,
    httpDetail: ex?.response?.data?.detail || null,
  };
  // Backend wraps Stripe SDK errors as 502 with detail "Stripe error: ..."
  if (out.httpDetail && !out.code) {
    const m = String(out.httpDetail);
    out.message = m;
  }
  return out;
}

/** Stable single-line string for logging + UI compact view. */
function summarizeFailure(f) {
  const bits = [
    `[${f.stage}]`,
    f.code && `code=${f.code}`,
    f.declineCode && `decline=${f.declineCode}`,
    f.type && `type=${f.type}`,
    f.subscriptionStatus && `sub=${f.subscriptionStatus}`,
    f.paymentIntentStatus && `pi=${f.paymentIntentStatus}`,
    f.setupIntentStatus && `si=${f.setupIntentStatus}`,
    f.requestId && `req=${f.requestId}`,
    f.httpStatus && `http=${f.httpStatus}`,
  ].filter(Boolean);
  return `${bits.join(" ")} — ${f.message}`;
}

/**
 * Fire-and-forget POST to /api/pay/log-failure with the diagnostic struct
 * the rider sees in the StripeFailurePanel. Mirrors the JS field names
 * 1:1 (the backend Pydantic model accepts the exact camelCase shape) so
 * the wire format is dead simple to trace.
 *
 * Never throws — logging must never break the user-facing error flow.
 */
function logFailureToServer(failure, extra = {}) {
  try {
    api.post("/pay/log-failure", { ...failure, ...extra }).catch((logErr) => {
      // Last resort — if even the log endpoint is unreachable we still
      // surface it in the rider's console so they/we can grab it manually.
      console.warn("[checkout-sheet] /pay/log-failure POST failed:", logErr?.message || logErr);
    });
  } catch (_e) { /* never block the catch handler */ }
}



/**
 * Stripe Elements bottom-sheet checkout.
 *
 * Mounted once the rider commits via slide-to-confirm. Never redirects —
 * card (+ Apple Pay / Google Pay when supported) is collected inline via
 * <PaymentElement>, then the PaymentIntent / SetupIntent is confirmed
 * client-side. On success we poll /api/pay/intent/{id}/status until the
 * backend confirms activation, then flip to a success state and close.
 *
 * When Elements truly can't render, `onFallback(reason)` bubbles up so the
 * parent can redirect the rider to hosted Stripe Checkout — they still pay.
 */

// Singleton Stripe.js promise, keyed by publishable key. Stripe's docs
// explicitly recommend calling `loadStripe()` once per key, at module scope.
// Calling it inside the component per-open triggers the "Stripe.js loaded
// more than one time" warning and, in some browsers, mounts duplicate
// m.stripe.network iframes that race and fail.
const stripeSingletons = new Map();
function getStripePromise(publishableKey) {
  if (!publishableKey) return null;
  if (!stripeSingletons.has(publishableKey)) {
    stripeSingletons.set(publishableKey, loadStripe(publishableKey));
  }
  return stripeSingletons.get(publishableKey);
}

export default function StripeCheckoutSheet({
  open,
  onClose,
  onSuccess,
  onFallback,         // (reason: string) => void  — when Elements truly can't render, route to hosted Checkout
  routeId,
  billingCycle,     // "weekly" | "payg"
  amountDisplay,    // preformatted "$123.45" for the header
  subtitle,         // "Weekly · Link Commute · 4 days"
  inline = false,   // when true, render in-flow (no fixed overlay/backdrop). Used by the dedicated /checkout/plan page.
  chromeless = false, // when true, skip the outer card chrome AND the "Secure Checkout" header — used by the in-sheet Review & Pay wizard step so the payment surface reads as part of the parent card. Implies inline.
  hideCloseButton = false, // when true, skip the X button in the header — parent supplies its own nav.
  // When the parent (e.g. /checkout/plan) owns the pricing breakdown +
  // promo entry, it passes these so the sheet doesn't duplicate them.
  hidePromo = false,
  externalPromoCode = null,      // string | null — parent-controlled promo code
  onIntentReady,                 // (intent) => void — fires after each /pay/intent fetch
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [intent, setIntent] = useState(null);   // {intent_kind, client_secret, intent_id, publishable_key, ...}
  const [stripePromise, setStripePromise] = useState(null);
  const [success, setSuccess] = useState(false);
  // Promotion code state — set when the rider applies one and we
  // recreate the intent with the discount; null otherwise.
  const [promoCode, setPromoCode] = useState(null);          // applied code string
  const [promoMeta, setPromoMeta] = useState(null);          // { label, savings }
  // Stable ref for onFallback — avoids re-running the intent fetch every
  // parent render (onFallback is re-created inline in JSX).
  const onFallbackRef = useRef(onFallback);
  useEffect(() => { onFallbackRef.current = onFallback; }, [onFallback]);

  // Reset promo state when sheet closes so reopening starts fresh.
  useEffect(() => {
    if (!open) {
      setPromoCode(null);
      setPromoMeta(null);
    }
  }, [open]);

  // Bootstrap: when sheet opens, ask backend for a fresh intent + publishable
  // key. We purposely re-fetch each open so stale client_secrets never get
  // reused after the rider backs out and returns. If anything fails we
  // fall back to hosted Checkout via `onFallback` so the rider can still pay.
  useEffect(() => {
    if (!open || !routeId) return;
    let cancelled = false;
    // When parent supplies an externalPromoCode, prefer it over the
    // sheet's internal promoCode (parent owns the affordance).
    const effectivePromo = externalPromoCode ?? promoCode;
    const run = async () => {
      setLoading(true);
      setError(null);
      setIntent(null);
      setSuccess(false);      try {
        console.info(`[checkout-sheet] requesting /pay/intent for route=${routeId} cycle=${billingCycle}`);
        // IMPORTANT: path is /pay/intent (NOT /stripe/intent). Ad blockers
        // like uBlock, AdBlock Plus, Brave Shields block any URL containing
        // "stripe" in the path. Backend exposes both; frontend uses /pay/*.
        const { data } = await api.post("/pay/intent", {
          route_id: routeId,
          billing_cycle: billingCycle,
          origin_url: window.location.origin,
          ...(effectivePromo ? { promo_code: effectivePromo } : {}),
        });
        if (cancelled) return;
        if (!data?.client_secret) {
          throw new Error("No client_secret returned from /pay/intent");
        }
        if (!data.publishable_key) {
          throw new Error("Elements checkout is not configured — missing publishable key.");
        }
        console.info(`[checkout-sheet] intent ok kind=${data.intent_kind} id=${data.intent_id} amount=$${data.amount}`);
        // Singleton Stripe.js promise (cached per publishable key) so <Elements>
        // gets a stable promise reference and we never trigger the
        // "Stripe.js loaded more than once" warning.
        const promise = getStripePromise(data.publishable_key);
        // Only *fall back* if Stripe.js ACTUALLY fails to load (network /
        // CSP / ad blocker). A brief race where the promise is still
        // pending is fine — <Elements> will await it internally.
        promise.then((s) => {
          if (cancelled) return;
          if (!s) {
            console.error("[checkout-sheet] Stripe.js failed to initialize (loadStripe resolved null)");
            onFallbackRef.current?.("stripe_js_load_failed");
          } else {
            console.info("[checkout-sheet] Stripe.js initialized successfully");
          }
        }).catch((stripeLoadErr) => {
          if (cancelled) return;
          console.error("[checkout-sheet] Stripe.js failed to load:", stripeLoadErr);
          onFallbackRef.current?.("stripe_js_load_failed");
        });
        setStripePromise(promise);
        setIntent(data);
        // Notify parent (e.g. /checkout/plan) so it can render the
        // pricing breakdown + applied-discount badge without re-fetching.
        try { onIntentReady?.(data); } catch (_) { /* parent error */ }
        if (data.promo?.applied) {
          setPromoMeta({ label: data.promo.label, savings: data.promo.savings });
        } else if (!effectivePromo) {
          // Fresh intent without applied promo → clear any stale meta.
          setPromoMeta(null);
        }
      } catch (e) {
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail;
        const msg = formatApiError(detail) || e.message || "Unable to start checkout";
        // Pull the structured `code` (e.g. "WAITLIST", "REJECTED") out of the
        // backend detail when present so the parent fallback handler can
        // route the rider correctly without parsing the human message.
        const code = (detail && typeof detail === "object" && typeof detail.code === "string") ? detail.code : null;
        console.error(`[checkout-sheet] intent fetch FAILED status=${status} code=${code} msg=${msg}`, e);
        if (cancelled) return;
        // 400 from a bad promo code shouldn't blow up the sheet — clear the
        // applied promo so the rider can try again. ALL other failures still
        // bubble to the hosted-Checkout fallback.
        if (status === 400 && promoCode) {
          setPromoCode(null);
          setError(msg);
          return;
        }
        if (onFallbackRef.current) {
          const reasonParts = ["intent", status || "network", code, msg].filter(Boolean);
          const reason = reasonParts.join("_").slice(0, 120);
          onFallbackRef.current(reason);
          return;
        }
        setError(msg);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [open, routeId, billingCycle, promoCode, externalPromoCode]);

  // Close on ESC
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape" && !success) onClose?.(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose, success]);

  const clientSecret = intent?.client_secret || null;
  const elementsOptions = useMemo(() => {
    // Hard guard — NEVER hand a nullish clientSecret to <Elements>, which
    // would otherwise throw during mount and white-screen the app.
    if (!clientSecret || typeof clientSecret !== "string" || clientSecret.length < 10) return null;
    // Dark navy glass palette: navy surface, cream ink, orange accent. Keeps
    // the PaymentElement visually consistent with the slide-to-confirm
    // pill that launches this sheet.
    return {
      clientSecret,
      appearance: {
        theme: "night",
        variables: {
          colorPrimary: "#ed6500",
          colorBackground: "#0A1128",
          colorText: "#F9F7F4",
          colorTextSecondary: "rgba(249,247,244,0.60)",
          colorTextPlaceholder: "rgba(249,247,244,0.40)",
          colorDanger: "#ff6b6b",
          fontFamily: 'Outfit, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
          fontSizeBase: "15px",
          borderRadius: "14px",
          spacingUnit: "4px",
        },
        rules: {
          ".Input": {
            backgroundColor: "rgba(255,255,255,0.05)",
            border: "1px solid rgba(255,255,255,0.12)",
            color: "#F9F7F4",
          },
          ".Input:focus": {
            border: "1px solid #ed6500",
            boxShadow: "0 0 0 2px rgba(237,101,0,0.25)",
          },
          ".Input--invalid": { border: "1px solid #ff6b6b" },
          ".Label": {
            fontFamily: "Outfit, ui-sans-serif, system-ui, sans-serif",
            fontSize: "10px",
            textTransform: "uppercase",
            letterSpacing: "0.22em",
            color: "rgba(249,247,244,0.55)",
            fontWeight: 500,
          },
          ".Tab": {
            backgroundColor: "rgba(255,255,255,0.04)",
            border: "1px solid rgba(255,255,255,0.12)",
          },
          ".Tab:hover": { backgroundColor: "rgba(255,255,255,0.07)" },
          ".Tab--selected": { backgroundColor: "#ed6500", borderColor: "#ed6500", color: "#fff" },
          ".TabIcon--selected": { fill: "#fff" },
          ".AccordionItem": {
            backgroundColor: "rgba(255,255,255,0.04)",
            border: "1px solid rgba(255,255,255,0.12)",
          },
          ".Error": { color: "#ff6b6b", fontSize: "12px" },
        },
      },
    };
  }, [clientSecret]);

  // Diagnostic log — helps the user paste a single console line if
  // anything goes sideways.
  useEffect(() => {
    if (!open) return;
    console.log("[checkout-sheet] state:", {
      open,
      has_intent: !!intent,
      intent_kind: intent?.intent_kind,
      has_client_secret: !!clientSecret,
      client_secret_prefix: clientSecret ? clientSecret.slice(0, 12) + "…" : null,
      has_stripe_promise: !!stripePromise,
      loading,
      error,
    });
  }, [open, intent, clientSecret, stripePromise, loading, error]);

  // Inline mode is in-flow and renders synchronously; no exit animation
  // needed because the parent's checkout page swaps content.
  if (inline || chromeless) {
    if (!open) return null;
    if (chromeless) {
      // No outer card chrome — the parent (BookingSheet ReviewStep)
      // already lives in a card so a nested one causes double-borders
      // and visual competition with the wizard's own frame.
      return (
        <div data-testid="checkout-sheet" data-inline="true" data-chromeless="true">
          {renderSheetBody()}
        </div>
      );
    }
    return (
      <div data-testid="checkout-sheet" data-inline="true">
        <div
          className="relative w-full bg-[#0A1128] text-[#F9F7F4] rounded-3xl border border-white/10 shadow-xl overflow-hidden"
          data-testid="checkout-sheet-panel"
        >
          {renderSheetBody()}
        </div>
      </div>
    );
  }

  // Sheet mode — wrap with AnimatePresence so the close animates instead
  // of unmounting instantly. The inner motion.div slides up on mount and
  // slides down on exit, mirroring the rest of the Motion V2 sheets.
  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center" data-testid="checkout-sheet">
          <CheckoutBackdrop onClick={success ? undefined : onClose} />
          <motion.div
            className="relative w-full sm:max-w-md max-h-[92dvh] overflow-y-auto bg-[#0A1128]/95 backdrop-blur-xl text-[#F9F7F4] rounded-t-[24px] sm:rounded-[24px] border border-white/10 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.6)]"
            role="dialog"
            aria-modal="true"
            data-testid="checkout-sheet-panel"
            style={{ paddingBottom: "max(env(safe-area-inset-bottom), 0px)" }}
            initial={{ y: "100%", opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: "100%", opacity: 0 }}
            transition={{ type: "spring", stiffness: 320, damping: 32, mass: 0.9 }}
          >
            {renderSheetBody()}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );

  function renderSheetBody() {
    return (
      <>
        <style>{`
          @keyframes reveal-methods { from { max-height: 0; opacity: 0; } to { max-height: 900px; opacity: 1; } }
          .animate-reveal { animation: reveal-methods 380ms cubic-bezier(0.22, 1, 0.36, 1) forwards; overflow: hidden; }
        `}</style>

        {/* drag handle (mobile, sheet-mode only) */}
        {!inline && !chromeless && (
          <div className="sm:hidden flex justify-center pt-2.5">
            <div className="w-10 h-1 rounded-full bg-white/20" />
          </div>
        )}

        {!chromeless && (
        <header className="px-6 pt-5 pb-4 flex items-start justify-between gap-4 border-b border-white/8">
          <div className="min-w-0">
            <div className="text-[10px] tracking-[0.28em] uppercase text-[#ed6500] font-medium">Secure Checkout</div>
            {subtitle && (
              <div className="text-[11px] tracking-[0.18em] uppercase text-white/50 mt-2 truncate">{subtitle}</div>
            )}
          </div>
          <div className="flex items-start gap-3 shrink-0">
            {/* Header amount — once the Stripe intent is created we
                source the dollar value DIRECTLY from intent.amount so a
                successful promo code immediately reflects in the big
                number. Falls back to the parent's amountDisplay (regular
                price) only before the intent is loaded. Without this,
                the header showed the un-discounted amount even after a
                valid coupon was applied — a confusing mismatch with the
                "$X off applied" badge below. */}
            <div className="font-serif text-[32px] leading-none" data-testid="checkout-sheet-amount">
              {intent && typeof intent.amount === "number"
                ? `$${Number(intent.amount).toFixed(2)}`
                : (amountDisplay || "—")}
            </div>
            {!hideCloseButton && (
              <button
                onClick={onClose}
                disabled={success}
                className="shrink-0 w-9 h-9 -mr-1 -mt-1 rounded-full text-white/60 hover:text-white hover:bg-white/5 flex items-center justify-center disabled:opacity-40 transition-colors"
                aria-label="Close checkout"
                data-testid="checkout-sheet-close"
              >
                <X size={18} strokeWidth={1.6} />
              </button>
            )}
          </div>
        </header>
        )}

        <div className={chromeless ? "px-0 py-0" : "px-6 py-6"}>
          {loading && (
            <div className="flex items-center justify-center py-16" data-testid="checkout-sheet-loading">
              <Loader2 size={22} strokeWidth={1.6} className="animate-spin text-white/60" />
            </div>
          )}
          {error && (
            <div className="py-10" data-testid="checkout-sheet-error">
              <div className="font-serif text-xl mb-1">We couldn&rsquo;t start checkout.</div>
              <div className="text-[13px] text-white/60 leading-relaxed mb-5">{error}</div>
              <button
                onClick={onClose}
                className="text-[10px] tracking-[0.28em] uppercase text-[#ed6500] hover:text-white transition-colors"
              >
                Close
              </button>
            </div>
          )}
          {success && (
            <div className="py-14 text-center" data-testid="checkout-sheet-success">
              <div className="mx-auto w-12 h-12 rounded-full border border-[#ed6500] bg-[#ed6500]/10 flex items-center justify-center mb-5">
                <Check size={20} strokeWidth={1.8} className="text-[#ed6500]" />
              </div>
              <div className="font-serif text-[28px] leading-tight mb-2">You&rsquo;re all set.</div>
              <div className="text-[11px] tracking-[0.18em] uppercase text-white/50">Your ride is scheduled</div>
            </div>
          )}
          {!loading && !error && !success && elementsOptions && stripePromise && intent && clientSecret && (
            <>
              {!hidePromo && (
                <PromoCodeRow
                  routeId={routeId}
                  billingCycle={billingCycle}
                  appliedCode={promoCode}
                  appliedMeta={promoMeta}
                  onApply={(code) => setPromoCode(code)}
                  onRemove={() => { setPromoCode(null); setPromoMeta(null); }}
                />
              )}
              <CheckoutErrorBoundary
                onError={(boundaryErr) => {
                  console.error("[checkout-sheet] ErrorBoundary caught:", boundaryErr);
                  setError(null);
                  onFallbackRef.current?.(`elements_crash_${(boundaryErr?.message || "unknown").slice(0, 60)}`);
                }}
              >
                <Elements stripe={stripePromise} options={elementsOptions}>
                  <StripeInnerForm
                    intent={intent}
                    onSuccess={() => {
                      setSuccess(true);
                      setTimeout(() => onSuccess?.(intent), 1200);
                    }}
                  />
                </Elements>
              </CheckoutErrorBoundary>
            </>
          )}
        </div>

        {!chromeless && (
          <footer className="px-6 pb-4 pt-3 flex items-center justify-center gap-2 text-[9px] tracking-[0.28em] uppercase text-white/40 border-t border-white/8">
            <ShieldCheck size={11} strokeWidth={1.6} /> Secured by Stripe
          </footer>
        )}
        {chromeless && (
          <div className="mt-2 flex items-center justify-center gap-1.5 text-[9px] tracking-[0.24em] uppercase text-white/35" aria-hidden="true">
            <ShieldCheck size={10} strokeWidth={1.6} /> Secured by Stripe
          </div>
        )}
      </>
    );
  }
}

/**
 * PromoCodeRow — minimal "Have a code?" affordance shown above the
 * PaymentElement. Collapsed by default so it doesn't compete visually
 * with wallets / card; expands to a small input + Apply pill on tap.
 *
 * Once a code is applied, the parent re-creates the Stripe intent with
 * the discount, the displayed amount updates, and this row flips to a
 * confirmation state with a Remove button.
 */
function PromoCodeRow({ routeId, billingCycle, appliedCode, appliedMeta, onApply, onRemove }) {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  // Success badge — small motion pill above the applied pill that
  // pulses in when a code first lands and fades away after ~1.8s.
  // The persistent applied pill stays as the always-visible
  // confirmation; the badge is the celebratory close-the-loop moment.
  const [justApplied, setJustApplied] = useState(false);
  const prevAppliedRef = useRef(null);
  useEffect(() => {
    // Only fire on transition from "no code" → "code applied" (or a
    // different code) — not on parent re-renders where the same code
    // is already sitting there.
    if (appliedCode && appliedCode !== prevAppliedRef.current) {
      setJustApplied(true);
      const t = setTimeout(() => setJustApplied(false), 1800);
      prevAppliedRef.current = appliedCode;
      return () => clearTimeout(t);
    }
    if (!appliedCode) prevAppliedRef.current = null;
    return undefined;
  }, [appliedCode]);

  // Keep the input focused on expand for fast entry on mobile.
  const inputRef = useRef(null);
  useEffect(() => { if (open && inputRef.current) inputRef.current.focus(); }, [open]);

  const apply = async (e) => {
    e?.preventDefault?.();
    const cleaned = code.trim();
    if (!cleaned || busy) return;
    setBusy(true);
    setErr(null);
    try {
      // Preview first — gives instant feedback ("Invalid code" / "$5 off")
      // without recreating the Stripe intent.
      const { data } = await api.post("/pay/promo/preview", {
        route_id: routeId, code: cleaned, billing_cycle: billingCycle,
      });
      if (!data.ok) {
        setErr(data.error || "That code isn't valid.");
        return;
      }
      // Hand the validated code to the parent — it re-fetches /pay/intent
      // with promo_code so Stripe creates a new intent with the discount.
      onApply(cleaned);
      setCode("");
      setOpen(false);
    } catch (e2) {
      const msg = formatApiError(e2.response?.data?.detail) || e2.message || "Couldn't validate this code.";
      setErr(msg);
    } finally {
      setBusy(false);
    }
  };

  // Applied state — small confirmation pill with remove affordance,
  // topped by a short-lived "APPLIED · saved $X.XX" success badge that
  // pulses in the moment the code lands and fades ~1.8s later.
  if (appliedCode && appliedMeta) {
    const savingsUsd = Number(appliedMeta.savings || 0);
    return (
      <div className="mb-3 space-y-2">
        <AnimatePresence>
          {justApplied && (
            <motion.div
              key="promo-success-badge"
              initial={{ opacity: 0, y: 6, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -4, scale: 0.98 }}
              transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#3ecf8e]/15 border border-[#3ecf8e]/40 text-[10px] tracking-[0.22em] uppercase font-semibold text-[#3ecf8e]"
              data-testid="checkout-sheet-promo-success-badge"
            >
              <Check size={11} strokeWidth={2.4} />
              <span>Applied{savingsUsd > 0 ? ` · saved $${savingsUsd.toFixed(2)}` : ""}</span>
            </motion.div>
          )}
        </AnimatePresence>
        <div
          className="flex items-center justify-between gap-3 px-4 py-3 rounded-2xl border border-[#ed6500]/40 bg-[#ed6500]/10"
          data-testid="checkout-sheet-promo-applied"
        >
          <div className="min-w-0">
            <div className="text-[10px] tracking-[0.28em] uppercase text-[#ed6500] font-medium">Code applied</div>
            <div className="text-[13px] text-white/90 truncate mt-0.5">
              <span className="font-medium tracking-wide">{appliedCode.toUpperCase()}</span>
              <span className="mx-1.5 text-white/40">·</span>
              <span className="text-white/70">{appliedMeta.label || `−$${savingsUsd.toFixed(2)}`}</span>
            </div>
          </div>
          <button
            type="button"
            onClick={onRemove}
            className="shrink-0 text-[10px] tracking-[0.28em] uppercase text-white/55 hover:text-white transition-colors"
            data-testid="checkout-sheet-promo-remove"
          >
            Remove
          </button>
        </div>
      </div>
    );
  }

  // Collapsed entry — full-width tappable row so it competes for
  // attention with the payment CTAs. The old "Have a code?" text link
  // read as a legal footnote and was routinely missed.
  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mb-3 w-full flex items-center justify-between gap-3 px-4 h-11 rounded-2xl bg-white/[0.04] border border-dashed border-white/15 hover:border-[#ed6500] hover:bg-[#ed6500]/[0.08] text-white/85 hover:text-white transition-colors"
        data-testid="checkout-sheet-promo-toggle"
      >
        <span className="inline-flex items-center gap-2 text-[12px] tracking-wide">
          <Tag size={14} strokeWidth={1.8} className="text-[#ed6500]" />
          <span>Have a promo code?</span>
        </span>
        <span className="text-[10px] tracking-[0.22em] uppercase text-white/50">Apply</span>
      </button>
    );
  }

  // Expanded form — input + Apply pill.
  return (
    <form onSubmit={apply} className="mb-3" data-testid="checkout-sheet-promo-form">
      <div className="flex gap-2">
        <input
          ref={inputRef}
          type="text"
          value={code}
          onChange={(e) => { setCode(e.target.value); setErr(null); }}
          placeholder="Enter promo code"
          autoCapitalize="characters"
          autoComplete="off"
          spellCheck="false"
          // text-[16px] minimum prevents iOS Safari from auto-zooming the
          // viewport when this input gains focus.  Safari ignores any
          // input under 16px and zooms in to 16px-equivalent visually,
          // which leaves the rider stranded mid-checkout.
          className="flex-1 h-12 px-4 bg-white/[0.04] border border-white/10 rounded-2xl text-[16px] tracking-wider uppercase placeholder:text-white/30 placeholder:tracking-[0.18em] placeholder:normal-case placeholder:text-[12px] focus:outline-none focus:border-[#ed6500] focus:bg-white/[0.06] transition-colors"
          data-testid="checkout-sheet-promo-input"
          disabled={busy}
        />
        <button
          type="submit"
          disabled={busy || !code.trim()}
          className="shrink-0 px-5 h-12 rounded-2xl bg-[#ed6500] hover:bg-[#ff7b1f] text-white text-[10px] tracking-[0.28em] uppercase font-medium disabled:opacity-50 transition-colors flex items-center justify-center"
          data-testid="checkout-sheet-promo-apply"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : "Apply"}
        </button>
      </div>
      {err && (
        <div className="mt-2 text-[12px] text-[#ff6b6b]" data-testid="checkout-sheet-promo-error">{err}</div>
      )}
    </form>
  );
}



function StripeInnerForm({ intent, onSuccess }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const [paymentRequest, setPaymentRequest] = useState(null);   // Stripe PR object (once canMakePayment → truthy)
  const [prStatus, setPrStatus] = useState("checking");          // "checking" | "available" | "unavailable"
  const pollRef = useRef(null);

  useEffect(() => {
    console.log("[checkout-sheet] Stripe initialized:", !!stripe);
    console.log("[checkout-sheet] Client secret:", intent?.client_secret ? intent.client_secret.slice(0, 12) + "…" : "(none)");
  }, [stripe, intent]);

  // Poll backend after confirm — when Stripe reports succeeded, our webhook
  // should have activated the route; we re-check here for synchronous UX.
  const pollStatus = useCallback(async (intentId) => {
    let attempts = 0;
    return await new Promise((resolve, reject) => {
      const tick = async () => {
        try {
          const { data } = await api.get(`/pay/intent/${intentId}/status`);
          if (data.payment_status === "paid") return resolve(data);
          if (++attempts >= 12) return reject(new Error("Confirmation timed out"));
          pollRef.current = setTimeout(tick, 2000);
        } catch (e) {
          if (++attempts >= 12) return reject(e);
          pollRef.current = setTimeout(tick, 2000);
        }
      };
      tick();
    });
  }, []);

  useEffect(() => () => { if (pollRef.current) clearTimeout(pollRef.current); }, []);

  // ---- SAVED CARDS — one-tap "Default card" pill ---------------------------
  // Repeat bookings are the #1 conversion lever here. If the rider already
  // has a card on file (every PAYG charge persists the PM via
  // `setup_future_usage="off_session"`; every weekly subscription saves it
  // via the SetupIntent flow), we surface a single tap pill at the top of
  // the sheet — "Visa •••• 4242 · Pay $24.50" — and confirm the existing
  // PaymentIntent/SetupIntent client-side with the saved PM ID. No card
  // form, no scrolling, no second screen. 3DS still works via Stripe.js
  // native handleNextAction path. Stripe docs:
  //   stripe.confirmCardPayment(secret, { payment_method: 'pm_xxx' })
  //   stripe.confirmCardSetup(secret,   { payment_method: 'pm_xxx' })
  const [savedCards, setSavedCards] = useState(null); // null=loading, []=none, [...]=ready
  const [activePmId, setActivePmId] = useState(null);
  const [changeOpen, setChangeOpen] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/me/payment-methods");
        if (cancelled) return;
        const methods = Array.isArray(data?.methods) ? data.methods : [];
        setSavedCards(methods);
        // Prefer the customer's Stripe default; else the first card on file.
        const def = data?.default_payment_method || null;
        const initial = methods.find((m) => m.id === def) || methods[0] || null;
        setActivePmId(initial?.id || null);
      } catch (_e) {
        if (!cancelled) { setSavedCards([]); setActivePmId(null); }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const activeCard = useMemo(
    () => (savedCards || []).find((m) => m.id === activePmId) || null,
    [savedCards, activePmId],
  );

  // If the rider has neither a saved card nor a browser wallet, auto-open
  // the accordion so the card form is immediately reachable (preserves
  // pre-existing UX for first-time payers). When EITHER a saved card or
  // a wallet is available, the accordion stays collapsed under a
  // "More payment methods" toggle to keep the sheet short and focused.
  useEffect(() => {
    if (savedCards == null) return; // still loading
    if (prStatus === "checking") return;
    if (savedCards.length === 0 && prStatus === "unavailable") {
      setMoreOpen(true);
    }
  }, [savedCards, prStatus]);

  // One-tap confirm using the rider's saved PM. Same SCA handling +
  // subscription finalization as the card-form path — just no card form.
  const handleSavedCardPay = async () => {
    if (!stripe || !activePmId || submitting) return;
    setSubmitting(true);
    setErr(null);
    const ctx = {
      stage: "saved_card_confirm",
      intentKind: intent.intent_kind,
      intentId: intent.intent_id,
      paymentMethodId: activePmId,
    };
    try {
      if (intent.intent_kind === "payment_intent") {
        ctx.stage = "stripe.confirmCardPayment (saved)";
        const { error: sErr, paymentIntent } = await stripe.confirmCardPayment(
          intent.client_secret,
          { payment_method: activePmId },
        );
        console.info("[checkout-sheet] saved-card confirmCardPayment result:",
          { error: sErr || null, paymentIntent_id: paymentIntent?.id, paymentIntent_status: paymentIntent?.status });
        if (sErr) throw sErr;
        if (paymentIntent) { ctx.paymentIntentId = paymentIntent.id; ctx.paymentIntentStatus = paymentIntent.status; }
        if (paymentIntent && paymentIntent.status !== "succeeded") {
          throw new Error(`Payment status: ${paymentIntent.status}`);
        }
      } else if (intent.intent_kind === "setup_intent") {
        ctx.stage = "stripe.confirmCardSetup (saved)";
        const { error: sErr, setupIntent } = await stripe.confirmCardSetup(
          intent.client_secret,
          { payment_method: activePmId },
        );
        console.info("[checkout-sheet] saved-card confirmCardSetup result:",
          { error: sErr || null, setupIntent_id: setupIntent?.id, setupIntent_status: setupIntent?.status });
        if (sErr) throw sErr;
        if (setupIntent) { ctx.setupIntentId = setupIntent.id; ctx.setupIntentStatus = setupIntent.status; }
        if (setupIntent && setupIntent.status !== "succeeded") {
          throw new Error(`Setup status: ${setupIntent.status}`);
        }
        ctx.stage = "POST /pay/finalize-subscription (saved)";
        const { data: fin } = await api.post(
          "/pay/finalize-subscription",
          { setup_intent_id: intent.intent_id },
        );
        console.info("[checkout-sheet] saved-card finalize-subscription result:", fin);
        if (fin) {
          ctx.subscriptionId = fin.subscription_id;
          ctx.subscriptionStatus = fin.status;
          ctx.paymentIntentStatus = fin.payment_intent_status;
          ctx.invoiceId = fin.invoice_id || null;
          ctx.paymentIntentId = fin.payment_intent_id || ctx.paymentIntentId;
        }
        if (fin?.requires_action && fin.client_secret) {
          ctx.stage = "stripe.confirmCardPayment (saved invoice SCA)";
          const { error: payErr, paymentIntent } = await stripe.confirmCardPayment(fin.client_secret);
          console.info("[checkout-sheet] saved-card invoice confirm result:",
            { error: payErr || null, paymentIntent_id: paymentIntent?.id, paymentIntent_status: paymentIntent?.status });
          if (payErr) throw payErr;
          if (paymentIntent) { ctx.paymentIntentId = paymentIntent.id; ctx.paymentIntentStatus = paymentIntent.status; }
          if (paymentIntent && paymentIntent.status !== "succeeded") {
            throw new Error(`Subscription payment status: ${paymentIntent.status}`);
          }
        } else if (fin?.status && !["active", "trialing"].includes(fin.status)) {
          // Defensive — even if backend says `already: true`, the only safe
          // success states are active/trialing. Anything else means the
          // charge didn't land and we MUST raise so the rider doesn't see
          // a fake confirmation screen.
          throw new Error(`Subscription not active (status: ${fin.status})`);
        }
      } else {
        throw new Error("Unknown intent kind");
      }
      try { await pollStatus(intent.intent_id); } catch { /* webhook catches up */ }
      onSuccess?.();
    } catch (ex) {
      const failure = extractStripeFailure(ex, ctx);
      console.error("[checkout-sheet] SAVED-CARD CONFIRM FAILED:", summarizeFailure(failure), { failure, raw: ex });
      logFailureToServer(failure);
      setErr(failure);
      toast.error(failure.message);
    } finally {
      setSubmitting(false);
    }
  };

  // ---- PAYMENT REQUEST (Apple Pay / Google Pay / browser wallet) ---------
  // Feature-detect ONLY — no UA sniffing. Stripe's canMakePayment() is
  // the source of truth: it interrogates the actual browser + saved
  // payment credentials and returns { applePay, googlePay, link, ... }
  // or null. That resolves iOS Chrome / Edge / Firefox (all WebKit,
  // Apple Pay eligible) and desktop Chrome with a saved Google Pay card
  // — both of which a UA gate would have wrongly excluded.
  //
  // We disable `browserCard` and `link` (opinionated: neither is a wallet
  // in the Apple Pay / Google Pay sense the rider expects here), but we
  // never disable applePay or googlePay preemptively — canMakePayment
  // decides.
  useEffect(() => {
    if (!stripe || !intent) return;
    const amountCents = Math.max(50, Math.round((intent.amount || 0) * 100));
    const pr = stripe.paymentRequest({
      country: "US",
      currency: (intent.currency || "usd").toLowerCase(),
      total: {
        label: intent.intent_kind === "setup_intent" ? "Link · Weekly ride plan" : "Link ride",
        amount: amountCents,
      },
      requestPayerName: true,
      requestPayerEmail: true,
      // Explicit opt-out — Link and browserCard aren't wallets in the
      // "one-tap with Face ID" sense; if the rider wants a saved card
      // that's the block below the wallet slot.
      disableWallets: ["browserCard", "link"],
    });

    pr.canMakePayment()
      .then((result) => {
        console.log("[checkout-sheet] paymentRequest.canMakePayment:", result);
        if (result) {
          // result is e.g. { applePay: true } — we got a wallet.
          setPaymentRequest(pr);
          setPrStatus("available");
        } else {
          setPrStatus("unavailable");
        }
      })
      .catch((e) => {
        console.warn("[checkout-sheet] canMakePayment failed:", e);
        setPrStatus("unavailable");
      });

    // On wallet confirm (rider taps Apple Pay / Google Pay / browser wallet,
    // authenticates with Face/Touch ID, and Stripe hands us the PaymentMethod).
    pr.on("paymentmethod", async (ev) => {
      setErr(null);
      setSubmitting(true);
      const ctx = {
        stage: "wallet_confirm",
        intentKind: intent.intent_kind,
        intentId: intent.intent_id,
        paymentMethodId: ev?.paymentMethod?.id,
      };
      try {
        if (intent.intent_kind === "payment_intent") {
          ctx.stage = "stripe.confirmCardPayment (wallet)";
          const { error: confErr, paymentIntent } = await stripe.confirmCardPayment(
            intent.client_secret,
            { payment_method: ev.paymentMethod.id },
            { handleActions: false },
          );
          console.info("[checkout-sheet] wallet confirmCardPayment result:",
            { error: confErr || null, paymentIntent_id: paymentIntent?.id, paymentIntent_status: paymentIntent?.status });
          if (confErr) { ev.complete("fail"); throw confErr; }
          ev.complete("success");
          if (paymentIntent) { ctx.paymentIntentId = paymentIntent.id; ctx.paymentIntentStatus = paymentIntent.status; }
          if (paymentIntent && paymentIntent.status === "requires_action") {
            ctx.stage = "stripe.confirmCardPayment (wallet 3DS)";
            const { error: actErr, paymentIntent: pi2 } = await stripe.confirmCardPayment(intent.client_secret);
            if (pi2) { ctx.paymentIntentStatus = pi2.status; }
            if (actErr) throw actErr;
          }
        } else if (intent.intent_kind === "setup_intent") {
          ctx.stage = "stripe.confirmCardSetup (wallet)";
          const { error: confErr, setupIntent } = await stripe.confirmCardSetup(
            intent.client_secret,
            { payment_method: ev.paymentMethod.id },
            { handleActions: false },
          );
          console.info("[checkout-sheet] wallet confirmCardSetup result:",
            { error: confErr || null, setupIntent_id: setupIntent?.id, setupIntent_status: setupIntent?.status });
          if (confErr) { ev.complete("fail"); throw confErr; }
          ev.complete("success");
          if (setupIntent) { ctx.setupIntentId = setupIntent.id; ctx.setupIntentStatus = setupIntent.status; }
          if (setupIntent && setupIntent.status === "requires_action") {
            ctx.stage = "stripe.confirmCardSetup (wallet 3DS)";
            const { error: actErr, setupIntent: si2 } = await stripe.confirmCardSetup(intent.client_secret);
            if (si2) { ctx.setupIntentStatus = si2.status; }
            if (actErr) throw actErr;
          }
          ctx.stage = "POST /pay/finalize-subscription (wallet)";
          const finResp = await api.post("/pay/finalize-subscription", { setup_intent_id: intent.intent_id });
          const fin = finResp?.data;
          console.info("[checkout-sheet] wallet finalize-subscription result:", fin);
          if (fin) {
            ctx.subscriptionId = fin.subscription_id;
            ctx.subscriptionStatus = fin.status;
            ctx.paymentIntentStatus = fin.payment_intent_status;
            ctx.invoiceId = fin.invoice_id || null;
            ctx.paymentIntentId = fin.payment_intent_id || ctx.paymentIntentId;
          }
          if (fin?.requires_action && fin.client_secret) {
            ctx.stage = "stripe.confirmCardPayment (wallet invoice SCA)";
            const { error: payErr, paymentIntent } = await stripe.confirmCardPayment(fin.client_secret);
            if (paymentIntent) { ctx.paymentIntentId = paymentIntent.id; ctx.paymentIntentStatus = paymentIntent.status; }
            if (payErr) throw payErr;
            if (paymentIntent && paymentIntent.status !== "succeeded") {
              throw new Error(`Subscription payment status: ${paymentIntent.status}`);
            }
          } else if (fin?.status && !["active", "trialing"].includes(fin.status) && !fin.already) {
            throw new Error(`Subscription not active (status: ${fin.status})`);
          }
        } else {
          ev.complete("fail");
          throw new Error("Unknown intent kind");
        }
        try { await pollStatus(intent.intent_id); } catch { /* webhook will catch up */ }
        onSuccess?.();
      } catch (payErr) {
        const failure = extractStripeFailure(payErr, ctx);
        console.error("[checkout-sheet] WALLET CONFIRM FAILED:", summarizeFailure(failure), { failure, raw: payErr });
        logFailureToServer(failure);
        setErr(failure);
        toast.error(failure.message);
      } finally {
        setSubmitting(false);
      }
    });
  }, [stripe, intent, onSuccess, pollStatus]);

  // ---- PAYMENT ELEMENT (card + all other methods) submit ------------------
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || submitting) return;
    setSubmitting(true);
    setErr(null);
    // Tracks the IDs we've learned so far, so when something later in the
    // chain blows up we can attach the subscription/invoice/PI we already
    // created to the diagnostic struct.
    const ctx = {
      stage: "confirm_payment_element",
      intentKind: intent.intent_kind,
      intentId: intent.intent_id,
    };
    try {
      const common = {
        elements,
        redirect: "if_required",
        confirmParams: { return_url: `${window.location.origin}/checkout/success?intent_id=${intent.intent_id}` },
      };
      if (intent.intent_kind === "payment_intent") {
        ctx.stage = "stripe.confirmPayment";
        const { error: sErr, paymentIntent } = await stripe.confirmPayment(common);
        console.info("[checkout-sheet] confirmPayment result:",
          { error: sErr || null, paymentIntent_id: paymentIntent?.id, paymentIntent_status: paymentIntent?.status });
        if (sErr) throw sErr;
        if (paymentIntent) {
          ctx.paymentIntentId = paymentIntent.id;
          ctx.paymentIntentStatus = paymentIntent.status;
          ctx.paymentMethodId = paymentIntent.payment_method;
        }
        if (paymentIntent && paymentIntent.status !== "succeeded") {
          throw new Error(`Payment status: ${paymentIntent.status}`);
        }
      } else if (intent.intent_kind === "setup_intent") {
        ctx.stage = "stripe.confirmSetup";
        const { error: sErr, setupIntent } = await stripe.confirmSetup(common);
        console.info("[checkout-sheet] confirmSetup result:",
          { error: sErr || null, setupIntent_id: setupIntent?.id, setupIntent_status: setupIntent?.status, payment_method: setupIntent?.payment_method });
        if (sErr) throw sErr;
        if (setupIntent) {
          ctx.setupIntentId = setupIntent.id;
          ctx.setupIntentStatus = setupIntent.status;
          ctx.paymentMethodId = setupIntent.payment_method;
        }
        if (setupIntent && setupIntent.status !== "succeeded") {
          throw new Error(`Setup status: ${setupIntent.status}`);
        }
        // Backend creates the Subscription with `default_incomplete`.  The
        // sub goes to `active` immediately if the saved card charged cleanly,
        // but if Stripe requires SCA / 3DS the sub stays `incomplete` and we
        // get back a SECOND client_secret for the invoice's PaymentIntent.
        // We must confirm THAT one too — otherwise the invoice sits unpaid
        // in Stripe and the user bounces back to the payment screen with no
        // visible error.  (This was the "slide to confirm → processing →
        // back to payment" bug.)
        ctx.stage = "POST /pay/finalize-subscription";
        const { data: fin } = await api.post("/pay/finalize-subscription", { setup_intent_id: intent.intent_id });
        console.info("[checkout-sheet] finalize-subscription result:", fin);
        if (fin) {
          ctx.subscriptionId = fin.subscription_id;
          ctx.subscriptionStatus = fin.status;
          ctx.paymentIntentStatus = fin.payment_intent_status;
          ctx.invoiceId = fin.invoice_id || null;
          ctx.paymentIntentId = fin.payment_intent_id || ctx.paymentIntentId;
        }
        if (fin?.requires_action && fin.client_secret) {
          ctx.stage = "stripe.confirmCardPayment (subscription invoice SCA)";
          const { error: payErr, paymentIntent } = await stripe.confirmCardPayment(fin.client_secret);
          console.info("[checkout-sheet] invoice confirm result:",
            { error: payErr || null, paymentIntent_id: paymentIntent?.id, paymentIntent_status: paymentIntent?.status });
          if (payErr) throw payErr;
          if (paymentIntent) {
            ctx.paymentIntentId = paymentIntent.id;
            ctx.paymentIntentStatus = paymentIntent.status;
          }
          if (paymentIntent && paymentIntent.status !== "succeeded") {
            throw new Error(`Subscription payment status: ${paymentIntent.status}`);
          }
        } else if (fin?.status && !["active", "trialing"].includes(fin.status) && !fin.already) {
          // No second client_secret returned AND sub isn't active — something
          // went wrong server-side.  Surface it instead of silently succeeding.
          throw new Error(`Subscription not active (status: ${fin.status})`);
        }
      } else {
        throw new Error("Unknown intent kind");
      }
      try { await pollStatus(intent.intent_id); } catch { /* webhook catches up */ }
      onSuccess?.();
    } catch (ex) {
      const failure = extractStripeFailure(ex, ctx);
      console.error("[checkout-sheet] CONFIRM FAILED:", summarizeFailure(failure), { failure, raw: ex });
      logFailureToServer(failure);
      setErr(failure);
      toast.error(failure.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="checkout-sheet-form">
      {/* ------------------------------------------------------------------
          [1] PRIMARY — Apple Pay / Google Pay / browser wallet.
          Reordered to the TOP so returning riders see one-tap payment
          without scrolling (Round · Step 3 fit-no-scroll acceptance).
          Rendered only when `canMakePayment()` returns a truthy wallet
          — unsupported devices get NO empty slot, they fall through to
          the saved-card row below.
          Descendancy: this element is still a child of the enclosing
          <Elements> provider (same provider as PaymentElement below).
      ------------------------------------------------------------------ */}
      {prStatus === "checking" && (
        <div className="flex items-center justify-center py-3" data-testid="checkout-sheet-wallets-loading">
          <Loader2 size={16} className="animate-spin text-white/50" />
        </div>
      )}
      {prStatus === "available" && paymentRequest && (
        <div data-testid="checkout-sheet-wallets">
          <PaymentRequestButtonElement
            options={{
              paymentRequest,
              style: {
                paymentRequestButton: {
                  type: intent.intent_kind === "setup_intent" ? "subscribe" : "default",
                  theme: "dark",
                  height: "52px",
                },
              },
            }}
          />
        </div>
      )}

      {/* ------------------------------------------------------------------
          [2] PAYMENT METHOD — one consolidated dropdown row that
          replaces the previous split of "Tap to pay" (immediate charge
          CTA) + "Use a different method" (dropdown toggle). The row
          now shows the currently selected saved card and a chevron;
          tapping it opens the same dropdown that lists the rider's
          other saved cards, "Add new payment method", and (via the
          Stripe PaymentElement `save` toggle when adding a new card)
          the option to save + attach to the wallet for next time.
          The actual charge happens on the "Pay $X.XX" submit button
          rendered below.
      ------------------------------------------------------------------ */}
      {savedCards && savedCards.length > 0 && activeCard && (
        <div className="space-y-2" data-testid="checkout-saved-card-block">
          <button
            type="button"
            onClick={() => setChangeOpen((v) => !v)}
            disabled={submitting}
            className="w-full flex items-center justify-between gap-3 px-4 py-3.5 rounded-2xl bg-white/[0.04] hover:bg-white/[0.07] border border-white/12 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            data-testid="checkout-payment-method-toggle"
            aria-expanded={changeOpen}
            aria-label="Choose payment method"
          >
            <span className="flex items-center gap-3 min-w-0">
              <CardBrandGlyph brand={activeCard.brand} />
              <span className="flex flex-col items-start min-w-0">
                <span className="text-[10px] tracking-[0.28em] uppercase text-white/55">
                  Payment method
                </span>
                <span className="text-[14px] font-medium tracking-wide truncate text-white">
                  {(activeCard.brand || "Card").replace(/^./, (c) => c.toUpperCase())} •••• {activeCard.last4}
                </span>
              </span>
            </span>
            <ChevronDown
              size={16}
              strokeWidth={1.8}
              className={`text-white/70 shrink-0 transition-transform ${changeOpen ? "rotate-180" : ""}`}
            />
          </button>
        </div>
      )}

      {/* Consolidated dropdown — renders when the rider taps
          "Use a different method". Contains any other saved cards +
          "Add new payment method" (which unfolds the PaymentElement
          inline). One dropdown, one place, no second "More payment
          methods" button living below the wallet. */}
      {savedCards && savedCards.length > 0 && changeOpen && (
        <div className="space-y-1.5 rounded-2xl border border-white/10 bg-white/[0.03] p-2" data-testid="checkout-saved-card-list">
          {savedCards.filter((m) => m.id !== activePmId).map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => { setActivePmId(m.id); setChangeOpen(false); setMoreOpen(false); }}
              className="w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl bg-white/[0.03] hover:bg-white/[0.07] border border-white/10 transition-colors"
              data-testid={`checkout-saved-card-option-${m.last4}`}
            >
              <span className="flex items-center gap-3 min-w-0">
                <CardBrandGlyph brand={m.brand} />
                <span className="text-[13px] tracking-wide text-white/90 truncate">
                  {(m.brand || "Card").replace(/^./, (c) => c.toUpperCase())} •••• {m.last4}
                </span>
              </span>
              <span className="text-[10px] tracking-[0.22em] uppercase text-white/40">Use</span>
            </button>
          ))}
          {/* Add-new toggle — flips moreOpen so PaymentElement mounts
              inline below. When it's already open, the toggle collapses
              it again. */}
          <button
            type="button"
            onClick={() => setMoreOpen((v) => !v)}
            className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl border transition-colors ${
              moreOpen
                ? "border-[#ed6500]/40 bg-[#ed6500]/[0.08]"
                : "border-dashed border-white/20 bg-transparent hover:bg-white/[0.04]"
            }`}
            data-testid="checkout-saved-card-add-new"
            aria-expanded={moreOpen}
          >
            <span className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-white/80">
              <Plus size={13} strokeWidth={2.2} className={moreOpen ? "text-[#ed6500]" : ""} />
              Add new payment method
            </span>
            <ChevronDown
              size={13}
              strokeWidth={1.8}
              className={`text-white/60 transition-transform ${moreOpen ? "rotate-180" : ""}`}
            />
          </button>
        </div>
      )}

      {/* ---- Fallback path: no saved cards yet.
          Older behaviour surfaced a separate "More payment methods"
          button below the wallet. Consolidated: if the rider has no
          saved cards, the PaymentElement accordion opens automatically
          (see the auto-open effect above) so their only path forward
          is the wallet (if supported) OR the card form — no third
          button competing for the tap. */}

      {moreOpen && (
        <div className="animate-reveal" data-testid="checkout-sheet-more-panel">
          <PaymentElement
            options={{
              layout: "accordion",
              // Phase C1 payment method order — Cash App Pay first, then
              // Card. Apple Pay + Google Pay live ABOVE this element
              // (see PaymentRequestButtonElement) so we suppress the
              // wallet auto-surfacing here to avoid double-rendering.
              paymentMethodOrder: ["cashapp", "card"],
              wallets: { applePay: "never", googlePay: "never" },
              defaultValues: { billingDetails: { name: "" } },
            }}
            data-testid="checkout-sheet-payment-element"
          />
        </div>
      )}

      {err && <StripeFailurePanel failure={err} />}

      {/* Primary charge CTA for saved-card path. Rendered when the
          rider has a saved card selected and is NOT currently adding
          a new method (moreOpen=false). This is the merged action
          that used to live on the top "Tap to pay" button before the
          payment-method row became a pure dropdown selector. */}
      {savedCards && savedCards.length > 0 && activeCard && !moreOpen && (
        <button
          type="button"
          onClick={handleSavedCardPay}
          disabled={!stripe || submitting}
          className="w-full h-14 rounded-2xl bg-[#ed6500] hover:bg-[#ff7b1f] text-white text-[11px] tracking-[0.32em] uppercase font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-all active:scale-[0.99] flex items-center justify-center gap-2"
          data-testid="checkout-saved-card-pay"
          style={{ fontFamily: "var(--font-display)" }}
        >
          {submitting ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 size={14} strokeWidth={1.8} className="animate-spin" /> Processing
            </span>
          ) : (
            <span>
              {intent.intent_kind === "setup_intent" ? "Start weekly plan" : "Pay"}
              {typeof intent.amount === "number" ? ` · $${Number(intent.amount).toFixed(2)}` : ""}
            </span>
          )}
        </button>
      )}

      {moreOpen && (
        <button
          type="submit"
          disabled={!stripe || !elements || submitting}
          className="w-full h-14 rounded-2xl bg-[#ed6500] hover:bg-[#ff7b1f] text-white text-[11px] tracking-[0.32em] uppercase font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-all active:scale-[0.99] flex items-center justify-center gap-2"
          data-testid="checkout-sheet-submit"
        >
          {submitting ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 size={14} strokeWidth={1.8} className="animate-spin" /> Processing
            </span>
          ) : (
            <span>{intent.intent_kind === "setup_intent" ? "Start weekly plan" : "Pay now"}</span>
          )}
        </button>
      )}
    </form>
  );
}

/**
 * Tiny brand glyph for the saved-card pill — kept as a text pill rather
 * than vendored SVGs so we don't ship trademark assets. Stripe returns
 * the card.brand as a lowercase token ("visa", "mastercard", "amex"…).
 */
function CardBrandGlyph({ brand }) {
  const b = (brand || "card").toLowerCase();
  const label = b === "amex" ? "Amex"
    : b === "mastercard" ? "MC"
    : b === "discover" ? "Disc"
    : b === "visa" ? "Visa"
    : "Card";
  return (
    <span className="inline-flex items-center justify-center min-w-[42px] h-7 px-2 rounded-md bg-white/15 text-white text-[10px] font-bold tracking-[0.12em] uppercase shrink-0">
      {label}
    </span>
  );
}

/**
 * StripeFailurePanel
 * ---------------------------------------------------------------------
 * Replaces the old single-line `<div className="text-[#ff6b6b]">{err}</div>`
 * red message. Renders the human Stripe message at the top, then the full
 * diagnostic struct (code / decline_code / type / subscription_id /
 * invoice_id / payment_intent_id / *_status / request_id / stage) in a
 * monospaced details block the rider can screenshot and paste to support.
 * This is the support-ticket diagnostic — every relevant Stripe ID and
 * status is visible.
 *
 * `failure` is the object produced by extractStripeFailure().
 */
function StripeFailurePanel({ failure }) {
  if (!failure) return null;
  const f = typeof failure === "string" ? { message: failure } : failure;
  const rows = [
    ["Stage", f.stage],
    ["Code", f.code],
    ["Decline code", f.declineCode],
    ["Type", f.type],
    ["HTTP status", f.httpStatus],
    ["HTTP detail", f.httpDetail],
    ["Intent kind", f.intentKind],
    ["Intent ID", f.intentId],
    ["Payment method", f.paymentMethodId],
    ["SetupIntent ID", f.setupIntentId],
    ["SetupIntent status", f.setupIntentStatus],
    ["PaymentIntent ID", f.paymentIntentId],
    ["PaymentIntent status", f.paymentIntentStatus],
    ["Subscription ID", f.subscriptionId],
    ["Subscription status", f.subscriptionStatus],
    ["Invoice ID", f.invoiceId],
    ["Request ID", f.requestId],
  ].filter(([, v]) => v !== null && v !== undefined && v !== "");
  return (
    <div
      role="alert"
      className="rounded-2xl border border-[#ff6b6b]/40 bg-[#ff6b6b]/[0.06] p-4 space-y-3"
      data-testid="checkout-sheet-err"
    >
      <div className="text-[13px] text-[#ff6b6b] font-medium leading-snug" data-testid="checkout-sheet-err-message">
        {f.message || "Payment failed"}
      </div>
      {rows.length > 0 && (
        <details className="text-[11px] text-white/70" data-testid="checkout-sheet-err-details">
          <summary className="cursor-pointer text-white/55 hover:text-white tracking-[0.22em] uppercase text-[10px] font-semibold">
            Show diagnostic details
          </summary>
          <dl className="mt-2 grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1 font-mono break-all">
            {rows.map(([k, v]) => (
              <React.Fragment key={k}>
                <dt className="text-white/45">{k}</dt>
                <dd className="text-white/85" data-testid={`checkout-sheet-err-field-${k.toLowerCase().replace(/\s+/g, "-")}`}>{String(v)}</dd>
              </React.Fragment>
            ))}
          </dl>
        </details>
      )}
    </div>
  );
}
